import { SVGProps, forwardRef, useEffect, useState } from 'react'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getTokenImage } from '@/ui/assets/tokens'
import { buildTokenImageSrcChain } from '../token-image/tokenImageFallback'

export interface TokenIconProps extends SVGProps<SVGSVGElement> {
  token: {
    symbol: TokenSymbol
    isAToken?: boolean
    logoURI?: string
    address?: string
  }
  isL1?: boolean
  disableEulerFallback?: boolean
}

const DEFAULT_FALLBACK = getTokenImage(TokenSymbol('unknown'))

interface FallbackSvgImageProps extends SVGProps<SVGImageElement> {
  srcChain: string[]
}

function FallbackSvgImage({ srcChain, ...rest }: FallbackSvgImageProps) {
  const [srcIndex, setSrcIndex] = useState(0)
  const srcChainKey = srcChain.join('|')

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when fallback chain content changes
  useEffect(() => {
    setSrcIndex(0)
  }, [srcChainKey])

  const currentSrc = srcChain[srcIndex] ?? DEFAULT_FALLBACK

  return (
    <image
      {...rest}
      href={currentSrc}
      onError={() => {
        setSrcIndex((current) => (current < srcChain.length - 1 ? current + 1 : current))
      }}
    />
  )
}

export const TokenIcon = forwardRef<SVGSVGElement, TokenIconProps>(
  ({ token, isL1: _isL1, disableEulerFallback = false, ...rest }, ref) => {
  if (token.isAToken) {
    const symbol = TokenSymbol(token.symbol.slice(1))

    return (
      <ATokenIcon
        symbol={symbol}
        logoURI={token.logoURI}
        tokenAddress={token.address}
        isL1={_isL1}
        disableEulerFallback={disableEulerFallback}
        {...rest}
        ref={ref}
      />
    )
  }

  const imageHref = getTokenImage(token.symbol, undefined, _isL1)
  const srcChain = buildTokenImageSrcChain({
    src: imageHref,
    fallbackSrc: token.logoURI,
    tokenAddress: token.address,
    defaultFallbackSrc: DEFAULT_FALLBACK,
    disableEulerFallback,
  })
  const srcChainKey = srcChain.join('|')

  return (
    <svg ref={ref} viewBox="0 0 256 256" {...rest}>
      <defs>
        <clipPath id="circleClip">
          <circle cx="128" cy="128" r="128" />
        </clipPath>
      </defs>
      <FallbackSvgImage
        key={srcChainKey}
        srcChain={srcChain}
        x="0"
        y="0"
        width="256"
        height="256"
        clipPath="url(#circleClip)"
      />
    </svg>
  )
},
)
TokenIcon.displayName = 'TokenIcon'

interface ATokenIconProps extends SVGProps<SVGSVGElement> {
  symbol: TokenSymbol
  logoURI?: string
  tokenAddress?: string
  isL1?: boolean
  disableEulerFallback?: boolean
}
const ATokenIcon = forwardRef<SVGSVGElement, ATokenIconProps>(
  ({ symbol, logoURI, tokenAddress, isL1, disableEulerFallback = false, ...rest }, ref) => {
    const imageHref = getTokenImage(symbol, undefined, isL1)
    const srcChain = buildTokenImageSrcChain({
      src: imageHref,
      fallbackSrc: logoURI,
      tokenAddress,
      defaultFallbackSrc: DEFAULT_FALLBACK,
      disableEulerFallback,
    })
    const srcChainKey = srcChain.join('|')

    return (
      <svg ref={ref} viewBox="0 0 256 256" {...rest}>
        <g>
          <circle cx="128" cy="128" r="120" fill="none" stroke="rgb(243, 151, 25)" strokeWidth="15" />
          <FallbackSvgImage key={srcChainKey} srcChain={srcChain} x="25" y="25" width="206" height="206" />
        </g>
      </svg>
    )
  },
)
ATokenIcon.displayName = 'ATokenIcon'
